import React, { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react'
import { Maximize2 } from 'lucide-react'
import useAuthStore from '../../stores/authStore'
import generationsApi from '../../services/generationsApi'
import PropTypes from 'prop-types'
import useLoadingStore from '../../stores/loadingStore'
import useMaskStore from '../../stores/maskStore'
import MaskPreview from './MaskPreview'
import { Spinner } from '../ui/spinner'


const ImagePreview = forwardRef(({ selectedImage, canvasSize, adjustments, onError, rightPanelVisible, isBrushing, brushMode, onBrushStroke, onCanvasSizeChange, showMaskPreview, previewMaskId }, ref) => {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const imageRef = useRef(null)
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 })
  const [showReposition, setShowReposition] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const token = useAuthStore(state => state.token)
  const brushCanvasRef = useRef(null)
  const isDrawing = useRef(false)
  const lastPoint = useRef(null)
  const [imageBounds, setImageBounds] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const prevImageDimensions = useRef({ width: 0, height: 0 })
  const isLoadingImages = useLoadingStore(state => state.isLoadingImages)
  const isGenerating = useLoadingStore(state => state.isGenerating)
  const generationProgress = useLoadingStore(state => state.generationProgress)
  const setIsLoadingImages = useLoadingStore(state => state.setIsLoadingImages)
  const [isImageLoading, setIsImageLoading] = useState(true)
  const [previewMaskImage, setPreviewMaskImage] = useState(null)
  const activeMaskId = useMaskStore(state => state.activeMaskId)
  const setActiveMask = useMaskStore(state => state.setActiveMask)

  // Add loading overlay component
  const LoadingOverlay = () => {
    if (!isImageLoading && !isGenerating) return null

    return (
      <div className="absolute inset-0 flex items-center justify-center bg-gray-900/75 backdrop-blur-sm">
        <div className="bg-gray-800 rounded-lg p-6 flex flex-col items-center gap-3">
          <Spinner size="md" className="text-blue-500" />
          <div className="text-white text-center">
            {isGenerating ? (
              <>
                <p className="font-medium">Generating Image{selectedImage?.versions?.length > 1 ? 's' : ''}</p>
                <p className="text-sm text-gray-400 mt-1">This may take a minute...</p>
                {generationProgress > 0 && (
                  <div className="w-full bg-gray-700 rounded-full h-1.5 mt-3">
                    <div 
                      className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                      style={{ width: `${generationProgress}%` }}
                    />
                  </div>
                )}
              </>
            ) : (
              <>
                <p className="font-medium">Loading Image</p>
                <p className="text-sm text-gray-400 mt-1">Please wait...</p>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }

  // Add wheel event handler for zooming
  const handleWheel = useCallback((e) => {
    if (isBrushing) return // Prevent zooming while brushing
    
    e.preventDefault()
    
    // Zoom speed factor - adjust this to control zoom sensitivity
    const ZOOM_SPEED = 0.05
    
    // Calculate new scale
    const delta = -e.deltaY
    const newScale = delta > 0 
      ? scale * (1 + ZOOM_SPEED)
      : scale / (1 + ZOOM_SPEED)
    
    // Limit min/max scale
    const MIN_SCALE = 0.1
    const MAX_SCALE = 3.0
    const clampedScale = Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE)
    
    // Get mouse position relative to canvas
    const rect = canvasRef.current.getBoundingClientRect()
    const mouseX = e.clientX - rect.left
    const mouseY = e.clientY - rect.top
    
    // Calculate new position to zoom towards mouse
    const scaleChange = clampedScale - scale
    const newPosition = {
      x: position.x - (mouseX - rect.width/2) * scaleChange / scale,
      y: position.y - (mouseY - rect.height/2) * scaleChange / scale
    }
    
    setScale(clampedScale)
    setPosition(newPosition)
  }, [scale, position, isBrushing])

  // Update calculateInitialScale to use a different scaling approach
  const calculateInitialScale = useCallback((imgWidth, imgHeight, canvasWidth, canvasHeight) => {
    if (!imgWidth || !imgHeight || !canvasWidth || !canvasHeight) {
      console.error('Invalid dimensions for scale calculation:', { 
        imgWidth, 
        imgHeight, 
        canvasWidth, 
        canvasHeight 
      })
      return 1
    }
    
    console.group('Scale Calculation Debug')
    console.log('Image dimensions:', { imgWidth, imgHeight })
    console.log('Canvas dimensions:', { canvasWidth, canvasHeight })
    
    // Calculate the scale needed to fit the image in the canvas with some padding
    const PADDING = 40 // pixels of padding on each side
    const availableWidth = canvasWidth - (PADDING * 2)
    const availableHeight = canvasHeight - (PADDING * 2)
    
    // Calculate scales that would fit the image within the available space
    const scaleX = availableWidth / imgWidth
    const scaleY = availableHeight / imgHeight
    
    // Use the smaller scale to ensure image fits in both dimensions
    const baseScale = Math.min(scaleX, scaleY)
    
    // Scale up the image to fill more of the canvas
    const SCALE_FACTOR = 1.0 // Adjust this value to make the image larger or smaller
    const finalScale = baseScale * SCALE_FACTOR
    
    console.log('Scale calculations:', {
      availableWidth,
      availableHeight,
      scaleX,
      scaleY,
      baseScale,
      finalScale,
      resultingWidth: imgWidth * finalScale,
      resultingHeight: imgHeight * finalScale
    })
    console.groupEnd()
    
    return finalScale
  }, [])

  // Update drawImage function to ensure we're not applying any additional scaling
  const drawImage = useCallback(() => {
    if (!imageRef.current || !canvasRef.current) {
      console.warn('DrawImage called without image or canvas')
      return
    }

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    
    const containerWidth = canvasSize.width || containerRef.current?.clientWidth || 800
    const containerHeight = canvasSize.height || containerRef.current?.clientHeight || 600

    // Set canvas size with device pixel ratio
    const dpr = window.devicePixelRatio || 1
    canvas.width = containerWidth * dpr
    canvas.height = containerHeight * dpr
    
    // Scale context for retina displays
    ctx.scale(dpr, dpr)

    // Clear canvas
    ctx.fillStyle = '#374151'
    ctx.fillRect(0, 0, containerWidth, containerHeight)

    console.group('DrawImage Debug')
    console.log('Canvas dimensions:', { containerWidth, containerHeight })
    console.log('Current scale:', scale)
    console.log('Current position:', position)
    console.log('Image natural dimensions:', { 
      width: imageRef.current.width, 
      height: imageRef.current.height 
    })

    // Calculate final dimensions
    const scaledWidth = imageRef.current.width * scale
    const scaledHeight = imageRef.current.height * scale
    
    // Center the image
    const x = (containerWidth - scaledWidth) / 2 + position.x
    const y = (containerHeight - scaledHeight) / 2 + position.y

    console.log('Final render dimensions:', { 
      scaledWidth, 
      scaledHeight,
      x,
      y,
      scale
    })

    // Store image bounds for brush constraints
    setImageBounds({
      x,
      y,
      width: scaledWidth,
      height: scaledHeight
    })

    // Draw the main image
    ctx.save()
    ctx.imageSmoothingEnabled = true
    ctx.imageSmoothingQuality = 'high'

    if (adjustments) {
      ctx.filter = `
        brightness(${1 + adjustments.exposure / 100})
        contrast(${1 + adjustments.contrast / 100})
        saturate(${1 + adjustments.highlights / 100})
        brightness(${1 + adjustments.shadows / 100})
      `
    }

    ctx.drawImage(
      imageRef.current,
      x, y,
      scaledWidth,
      scaledHeight
    )

    // Draw mask preview if enabled
    if (showMaskPreview && previewMaskImage) {
      ctx.globalAlpha = 0.5 // Set 50% opacity
      ctx.drawImage(
        previewMaskImage,
        x, y,
        scaledWidth,
        scaledHeight
      )
      ctx.globalAlpha = 1 // Reset opacity
    }

    if (isBrushing && brushCanvasRef.current) {
      ctx.globalCompositeOperation = 'destination-over'
      ctx.drawImage(brushCanvasRef.current, 0, 0)
      ctx.globalCompositeOperation = 'source-over'
    }

    ctx.restore()
    console.groupEnd()
  }, [canvasSize, scale, position, adjustments, isBrushing, showMaskPreview, previewMaskImage])

  // Add wheel event listener
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('wheel', handleWheel, { passive: false })
    return () => container.removeEventListener('wheel', handleWheel)
  }, [handleWheel])

  // Update the image loading effect
  useEffect(() => {
    if (!selectedImage?.src && !selectedImage?.selectedVersion?.src) {
      setIsImageLoading(false)
      return
    }

    if (!token) {
      setIsImageLoading(false)
      return
    }

    let mounted = true
    let objectUrls = null
    setIsImageLoading(true)
    setIsLoadingImages(true)

    const loadImage = async () => {
      try {
        const imageId = selectedImage.selectedVersion?.id || selectedImage.id
        objectUrls = await generationsApi.loadImagesInParallel([imageId], token)
        
        if (!mounted) return

        if (objectUrls.has(imageId)) {
          const img = new Image()
          
          img.onload = () => {
            if (!mounted) return
            
            imageRef.current = img
            setImageSize({
              width: img.naturalWidth,
              height: img.naturalHeight
            })
            
            setIsImageLoading(false)
            setIsLoadingImages(false)
            
            const needsNewScale = 
              !prevImageDimensions.current.width ||
              prevImageDimensions.current.width !== img.naturalWidth ||
              prevImageDimensions.current.height !== img.naturalHeight ||
              prevImageDimensions.current.canvasWidth !== canvasSize.width ||
              prevImageDimensions.current.canvasHeight !== canvasSize.height

            if (needsNewScale) {
              const initialScale = calculateInitialScale(
                img.naturalWidth,
                img.naturalHeight,
                canvasSize.width,
                canvasSize.height
              )
              
              setScale(initialScale)
              setPosition({ x: 0, y: 0 })
              
              prevImageDimensions.current = { 
                width: img.naturalWidth, 
                height: img.naturalHeight,
                canvasWidth: canvasSize.width,
                canvasHeight: canvasSize.height
              }
            }
            
            setTimeout(() => {
              if (mounted) {
                requestAnimationFrame(drawImage)
              }
            }, 50)
          }

          img.src = objectUrls.get(imageId)
        }
      } catch (error) {
        console.error('Failed to load image:', error)
        if (mounted) {
          setIsImageLoading(false)
          setIsLoadingImages(false)
          onError?.(error.message || 'Failed to load image. Please try again.')
        }
      }
    }

    loadImage()
    
    return () => {
      mounted = false
      setIsImageLoading(false)
      setIsLoadingImages(false)
      if (objectUrls) {
        generationsApi.revokeObjectUrls(objectUrls)
      }
    }
  }, [selectedImage?.selectedVersion?.src, selectedImage?.src, token, canvasSize, calculateInitialScale, onError, drawImage, setIsLoadingImages])

  // Update the drawImage effect to handle loading state
  useEffect(() => {
    if (!isImageLoading && imageRef.current) {
      requestAnimationFrame(drawImage)
    }
  }, [drawImage, isImageLoading])

  // Mouse/touch event handlers (keep existing implementations)
  const handleMouseDown = useCallback((e) => {
    if (e.button === 0) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }, [position])

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  // Add event listeners only once
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('mousedown', handleMouseDown)
    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('mouseup', handleMouseUp)

    return () => {
      container.removeEventListener('mousedown', handleMouseDown)
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('mouseup', handleMouseUp)
    }
  }, [handleMouseDown, handleMouseMove, handleMouseUp])

  const isProcessing = selectedImage?.status === 'processing'
  const showLoading = (isLoading && !imageRef.current) || isProcessing

  useEffect(() => {
    // Recalculate canvas size when right panel visibility changes
    if (canvasRef.current) {
      requestAnimationFrame(drawImage);
    }
  }, [rightPanelVisible]);

  // Add method to get adjusted image data
  const getAdjustedImageData = useCallback(() => {
    if (!imageRef.current) {
      throw new Error('No image loaded')
    }

    // Create temporary canvas at original image dimensions
    const tempCanvas = document.createElement('canvas')
    const tempCtx = tempCanvas.getContext('2d')
    
    // Set size to match original image
    tempCanvas.width = imageRef.current.naturalWidth
    tempCanvas.height = imageRef.current.naturalHeight

    // Apply adjustments
    if (adjustments) {
      tempCtx.filter = `
        brightness(${1 + adjustments.exposure / 100})
        contrast(${1 + adjustments.contrast / 100})
        saturate(${1 + adjustments.highlights / 100})
        brightness(${1 + adjustments.shadows / 100})
      `
    }

    // Draw the original image with adjustments
    tempCtx.drawImage(imageRef.current, 0, 0)

    // Return the image data as base64
    return tempCanvas.toDataURL('image/jpeg', 0.85)
  }, [adjustments])

  // Expose method to parent through ref
  useImperativeHandle(ref, () => ({
    getAdjustedImageData: () => {
      if (!imageRef.current) {
        throw new Error('No image loaded')
      }

      // Create temporary canvas at original image dimensions
      const tempCanvas = document.createElement('canvas')
      const tempCtx = tempCanvas.getContext('2d')
      
      // Set size to match original image
      tempCanvas.width = imageRef.current.naturalWidth
      tempCanvas.height = imageRef.current.naturalHeight

      // Apply adjustments
      if (adjustments) {
        tempCtx.filter = `
          brightness(${1 + adjustments.exposure / 100})
          contrast(${1 + adjustments.contrast / 100})
          saturate(${1 + adjustments.highlights / 100})
          brightness(${1 + adjustments.shadows / 100})
        `
      }

      // Draw the original image with adjustments
      tempCtx.drawImage(imageRef.current, 0, 0)

      return tempCanvas.toDataURL('image/jpeg', 0.85)
    },
    getBrushMask: () => {
      if (!brushCanvasRef.current || !imageRef.current || !imageBounds) {
        console.warn('Missing required refs for mask export')
        return null
      }
      
      console.group('Mask Export Debug')
      
      // Get the original image dimensions
      const originalWidth = imageRef.current.naturalWidth
      const originalHeight = imageRef.current.naturalHeight
      
      // Create the final mask canvas at original image dimensions
      const maskCanvas = document.createElement('canvas')
      maskCanvas.width = originalWidth
      maskCanvas.height = originalHeight
      const maskCtx = maskCanvas.getContext('2d')
      
      // Fill with black background
      maskCtx.fillStyle = 'black'
      maskCtx.fillRect(0, 0, originalWidth, originalHeight)
      
      // Create an intermediate canvas at the same size as the brush canvas
      const intermediateCanvas = document.createElement('canvas')
      intermediateCanvas.width = brushCanvasRef.current.width
      intermediateCanvas.height = brushCanvasRef.current.height
      const intermediateCtx = intermediateCanvas.getContext('2d')
      
      // Copy brush strokes to intermediate canvas
      intermediateCtx.drawImage(brushCanvasRef.current, 0, 0)
      
      // Get the device pixel ratio and container bounds
      const dpr = window.devicePixelRatio || 1
      const containerRect = canvasRef.current.getBoundingClientRect()
      
      // Calculate the scaling factors, accounting for container padding
      const displayToImageScaleX = originalWidth / (imageBounds.width * dpr)
      const displayToImageScaleY = originalHeight / (imageBounds.height * dpr)
      
      // Calculate the brush strokes position in image space, accounting for container offset
      const containerOffsetX = containerRect.left
      const containerOffsetY = containerRect.top
      const adjustedImageX = -(imageBounds.x - containerOffsetX)
      const adjustedImageY = -(imageBounds.y - containerOffsetY)
      
      // Add padding compensation (adjust these values if needed)
      const PADDING_COMPENSATION = 1.02 // 2% compensation for slight padding/margin effects
      const adjustedScaleX = displayToImageScaleX * PADDING_COMPENSATION
      const adjustedScaleY = displayToImageScaleY * PADDING_COMPENSATION
      
      console.log('Transform calculations:', {
        originalDimensions: { width: originalWidth, height: originalHeight },
        displayBounds: imageBounds,
        containerBounds: containerRect,
        dpr,
        scales: { 
          x: adjustedScaleX, 
          y: adjustedScaleY,
          original: { x: displayToImageScaleX, y: displayToImageScaleY }
        },
        imagePosition: { 
          x: adjustedImageX, 
          y: adjustedImageY,
          containerOffset: { x: containerOffsetX, y: containerOffsetY }
        }
      })
      
      // Draw debug border on intermediate canvas
      intermediateCtx.strokeStyle = 'red'
      intermediateCtx.lineWidth = 2
      intermediateCtx.strokeRect(0, 0, intermediateCanvas.width, intermediateCanvas.height)
      
      // Transform and draw the brush strokes onto the final mask
      maskCtx.save()
      
      // Reset any existing transformations
      maskCtx.setTransform(1, 0, 0, 1, 0, 0)
      
      // Scale to map from display space to image space with slight compensation
      maskCtx.scale(adjustedScaleX, adjustedScaleY)
      
      // Translate to align mask with image, using adjusted coordinates
      maskCtx.translate(adjustedImageX, adjustedImageY)
      
      // Draw the intermediate canvas
      maskCtx.drawImage(intermediateCanvas, 0, 0)
      
      maskCtx.restore()
      
      // Draw debug border on final mask
      maskCtx.strokeStyle = 'white'
      maskCtx.lineWidth = 1
      maskCtx.strokeRect(0, 0, maskCanvas.width, maskCanvas.height)
      
      // Log the final dimensions
      console.log('Final mask dimensions:', {
        width: maskCanvas.width,
        height: maskCanvas.height,
        imageData: maskCtx.getImageData(0, 0, maskCanvas.width, maskCanvas.height)
      })
      
      console.groupEnd()
      
      return maskCanvas.toDataURL('image/png')
    },
    exportImage: function() {
      return this.getAdjustedImageData()
    }
  }), [adjustments, imageBounds])

  // Initialize brush canvas when brushing starts
  useEffect(() => {
    if (isBrushing && !brushCanvasRef.current) {
      const brushCanvas = document.createElement('canvas')
      const dpr = window.devicePixelRatio || 1
      
      // Match the main canvas dimensions
      brushCanvas.width = canvasSize.width * dpr
      brushCanvas.height = canvasSize.height * dpr
      
      const ctx = brushCanvas.getContext('2d')
      
      // Scale for retina displays
      ctx.scale(dpr, dpr)
      
      // Initialize with black background
      ctx.fillStyle = 'black'
      ctx.fillRect(0, 0, brushCanvas.width, brushCanvas.height)
      
      brushCanvasRef.current = brushCanvas
      
      console.log('Brush canvas initialized:', {
        width: brushCanvas.width,
        height: brushCanvas.height,
        dpr,
        canvasSize
      })
    }
  }, [isBrushing, canvasSize])

  // Update cursor based on brush mode
  const getCursor = useCallback(() => {
    if (isBrushing) {
      const size = 24
      const color = 'white'
      const strokeWidth = 2
      
      return brushMode === 'erase'
        ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="${strokeWidth}"><circle cx="12" cy="12" r="6"/><line x1="8" y1="12" x2="16" y2="12"/></svg>') 12 12, crosshair`
        : `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="${strokeWidth}"><circle cx="12" cy="12" r="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="12" y1="8" x2="12" y2="16"/></svg>') 12 12, crosshair`
    }
    return isDragging ? 'grabbing' : 'grab'
  }, [isBrushing, isDragging, brushMode])

  // Add point within bounds check
  const isPointInBounds = useCallback((point) => {
    return (
      point.x >= imageBounds.x &&
      point.x <= imageBounds.x + imageBounds.width &&
      point.y >= imageBounds.y &&
      point.y <= imageBounds.y + imageBounds.height
    )
  }, [imageBounds])

  // Update pointer handlers for constrained brushing
  const handlePointerDown = useCallback((e) => {
    e.preventDefault()
    
    if (isBrushing && canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect()
      const point = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }
      
      isDrawing.current = true
      lastPoint.current = point
      
      if (isPointInBounds(point) && brushCanvasRef.current) {
        const ctx = brushCanvasRef.current.getContext('2d')
        ctx.globalCompositeOperation = brushMode === 'erase' ? 'destination-out' : 'source-over'
        
        // Save context state
        ctx.save()
        
        // Create clipping path for image bounds
        ctx.beginPath()
        ctx.rect(imageBounds.x, imageBounds.y, imageBounds.width, imageBounds.height)
        ctx.clip()
        
        // Set white color for brush strokes
        ctx.fillStyle = 'white'
        ctx.strokeStyle = 'white'
        
        // Draw the brush stroke
        ctx.beginPath()
        ctx.arc(point.x, point.y, 10, 0, Math.PI * 2)
        ctx.fill()
        
        // Restore context state
        ctx.restore()
        
        // Log stroke position for debugging
        console.log('Brush stroke:', {
          point,
          imageBounds,
          isInBounds: isPointInBounds(point)
        })
        
        onBrushStroke?.({
          type: 'dot',
          point,
          width: 20,
          mode: brushMode
        })
      }
    } else if (e.button === 0) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }, [isBrushing, brushMode, onBrushStroke, isPointInBounds, imageBounds])

  const handlePointerMove = useCallback((e) => {
    if (isDrawing.current && isBrushing && canvasRef.current && lastPoint.current) {
      const rect = canvasRef.current.getBoundingClientRect()
      const currentPoint = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      }

      if (brushCanvasRef.current) {
        const ctx = brushCanvasRef.current.getContext('2d')
        ctx.globalCompositeOperation = brushMode === 'erase' ? 'destination-out' : 'source-over'
        
        // Save context state
        ctx.save()
        
        // Create clipping path for image bounds
        ctx.beginPath()
        ctx.rect(imageBounds.x, imageBounds.y, imageBounds.width, imageBounds.height)
        ctx.clip()
        
        // Set white color for brush strokes
        ctx.strokeStyle = 'white'
        ctx.fillStyle = 'white'
        ctx.lineWidth = 20
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        
        // Draw the brush stroke
        ctx.beginPath()
        ctx.moveTo(lastPoint.current.x, lastPoint.current.y)
        ctx.lineTo(currentPoint.x, currentPoint.y)
        ctx.stroke()
        
        // Restore context state
        ctx.restore()

        // Log stroke positions for debugging
        console.log('Brush stroke:', {
          from: lastPoint.current,
          to: currentPoint,
          imageBounds,
          isFromInBounds: isPointInBounds(lastPoint.current),
          isToInBounds: isPointInBounds(currentPoint)
        })

        if (isPointInBounds(currentPoint) || isPointInBounds(lastPoint.current)) {
          onBrushStroke?.({
            type: 'stroke',
            from: lastPoint.current,
            to: currentPoint,
            width: 20,
            mode: brushMode
          })
        }
      }

      lastPoint.current = currentPoint
    } else if (isDragging && !isBrushing) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      })
    }
  }, [isDragging, dragStart, isBrushing, brushMode, onBrushStroke, isPointInBounds, imageBounds])

  const handlePointerUp = useCallback(() => {
    isDrawing.current = false
    if (!isBrushing) {
      setIsDragging(false)
    }
  }, [isBrushing])

  // Update canvas initialization
  useEffect(() => {
    const updateCanvas = () => {
      if (!canvasRef.current || !containerRef.current) return
      
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      
      // Get container dimensions
      const containerWidth = containerRef.current.clientWidth
      const containerHeight = containerRef.current.clientHeight

      // Set canvas size with device pixel ratio
      const dpr = window.devicePixelRatio || 1
      canvas.width = containerWidth * dpr
      canvas.height = containerHeight * dpr
      
      // Scale context for retina displays
      ctx.scale(dpr, dpr)

      // Update canvasSize prop if it doesn't match
      if (canvasSize.width !== containerWidth || canvasSize.height !== containerHeight) {
        onCanvasSizeChange?.({ width: containerWidth, height: containerHeight })
      }

      // Initialize brush canvas with same dimensions
      if (isBrushing && brushCanvasRef.current) {
        brushCanvasRef.current.width = containerWidth * dpr
        brushCanvasRef.current.height = containerHeight * dpr
        const brushCtx = brushCanvasRef.current.getContext('2d')
        brushCtx.scale(dpr, dpr)
        
        // Log canvas dimensions for debugging
        console.log('Canvas dimensions:', {
          container: { width: containerWidth, height: containerHeight },
          canvas: { width: canvas.width, height: canvas.height },
          brush: { 
            width: brushCanvasRef.current.width, 
            height: brushCanvasRef.current.height 
          },
          dpr
        })
      }
    }

    updateCanvas()
  }, [canvasSize.width, canvasSize.height, isBrushing, onCanvasSizeChange])

  // Add effect to load mask preview image when needed
  useEffect(() => {
    if (!showMaskPreview || !previewMaskId) {
      setPreviewMaskImage(null)
      return
    }

    let mounted = true
    const loadMaskPreview = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/masks/${previewMaskId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        })

        if (!response.ok) throw new Error('Failed to load mask preview')

        const blob = await response.blob()
        const img = new Image()
        
        img.onload = () => {
          if (mounted) {
            setPreviewMaskImage(img)
          }
        }
        
        img.src = URL.createObjectURL(blob)
      } catch (error) {
        console.error('Failed to load mask preview:', error)
      }
    }

    loadMaskPreview()

    return () => {
      mounted = false
    }
  }, [showMaskPreview, previewMaskId, token])

  // Clear active mask when image changes
  useEffect(() => {
    setActiveMask(null)
  }, [selectedImage?.id, setActiveMask])

  useEffect(() => {
    if (selectedImage?.id) {
      setPreviewMaskImage(null)
    }
  }, [selectedImage?.id])

  return (
    <div 
      ref={containerRef}
      className="w-full h-full relative overflow-hidden touch-none"
      style={{ 
        cursor: getCursor(),
        backgroundColor: '#374151',
        marginRight: rightPanelVisible ? '320px' : '0',
        transition: 'margin-right 0.2s ease-in-out'
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerLeave={handlePointerUp}
    >
      <canvas
        ref={canvasRef}
        style={{ 
          width: '100%',
          height: '100%',
          opacity: showLoading ? 0.5 : 1,
          transition: 'opacity 0.2s ease-in-out'
        }}
      />
      
      {isBrushing && (
        <>
          {/* Brush canvas */}
          <canvas
            ref={brushCanvasRef}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: 0.5,
              pointerEvents: 'none',
              mixBlendMode: brushMode === 'erase' ? 'destination-out' : 'normal'
            }}
          />
          
          {/* Status pills */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center gap-2">
            <div className="bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white">
              {brushMode === 'erase' ? 'Eraser Tool' : 'Brush Tool'}
            </div>
            <div className="bg-black/50 backdrop-blur-sm px-4 py-2 rounded-full text-sm text-white/75">
              Zooming and panning disabled while inpainting
            </div>
          </div>
        </>
      )}
      <LoadingOverlay />
    </div>
  )
})

// Separate LoadingIndicator component
const LoadingIndicator = React.memo(({ isProcessing }) => (
  <div className="absolute inset-0 flex items-center justify-center bg-gray-900/50">
    <div className="bg-gray-800 rounded-lg p-4 flex flex-col items-center gap-2">
      {isProcessing ? (
        <>
          <Spinner className="w-8 h-8 animate-spin text-gray-400" />
          <span className="text-gray-300">Generating image...</span>
        </>
      ) : (
        <>
          <Spinner className="w-5 h-5 animate-spin text-blue-500" />
          <span className="text-white">Loading image...</span>
        </>
      )}
    </div>
  </div>
))

// Add prop types if you're using them
ImagePreview.propTypes = {
  selectedImage: PropTypes.object,
  canvasSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number
  }),
  adjustments: PropTypes.object,
  onError: PropTypes.func,
  rightPanelVisible: PropTypes.bool,
  isBrushing: PropTypes.bool,
  brushMode: PropTypes.string,
  onBrushStroke: PropTypes.func,
  onCanvasSizeChange: PropTypes.func,
  showMaskPreview: PropTypes.bool,
  previewMaskId: PropTypes.string
}

export default React.memo(ImagePreview)